import { useCallback, useMemo, useState } from "react";
import { useNotify, useShowContext } from "react-admin";
import dataProvider from "server/dataProvider";
import { stripRowMeta } from "./rows";

// Each endpoint replaces a whole collection, so every action sends the full rebuilt list.
export const useCollectionMutations = (curriculum, collection) => {
  const notify = useNotify();
  const { refetch } = useShowContext();
  const [saving, setSaving] = useState(false);

  const items = useMemo(() => curriculum?.[collection.key] ?? [], [curriculum, collection.key]);

  const replaceAll = useCallback(
    async (nextItems, message) => {
      setSaving(true);
      try {
        await dataProvider.customRequest(collection.request(curriculum.id), { [collection.key]: nextItems }, "PUT");
        // useRefresh returns before the record is back; the next save would rebuild from stale data.
        await refetch();
        notify(message, { type: "success" });
      } finally {
        setSaving(false);
      }
    },
    [collection, curriculum?.id, notify, refetch],
  );

  const addItem = useCallback(
    data => replaceAll([...items, stripRowMeta(data)], "Записът е добавен успешно"),
    [items, replaceAll],
  );

  const updateItemAt = useCallback(
    (index, data) =>
      replaceAll(
        items.map((item, i) => (i === index ? { ...item, ...stripRowMeta(data) } : item)),
        "Записът е редактиран успешно",
      ),
    [items, replaceAll],
  );

  const removeItemAt = useCallback(
    index =>
      replaceAll(
        items.filter((_, i) => i !== index),
        "Записът е изтрит успешно",
      ),
    [items, replaceAll],
  );

  const updateNestedAt = useCallback(
    (index, key, nestedItems) =>
      replaceAll(
        items.map((item, i) => (i === index ? { ...item, [key]: nestedItems } : item)),
        "Промените са запазени успешно",
      ),
    [items, replaceAll],
  );

  return { items, saving, replaceAll, addItem, updateItemAt, removeItemAt, updateNestedAt };
};
